import { Button } from 'common/components/Button';
import { Modal } from 'common/components/Modal';
import { useEffect, useState, useRef, useCallback } from 'react';
import { CallTranscriptSpeech } from 'services/api/getCallTranscript';
import { useGetCallAudio, useGetCallTranscript } from 'services/hooks/mutations';
import { CallInfo } from 'types/agentCommandCenter.type';
import { getMockAudioUrl, getMockTranscript, getMockCallInfo } from './mockData';
import { getSpeakerType, Seconds, TranscriptionWithSpeaker, Transcripts } from './components/Transcript';
import styles from './SyncTranscript.module.scss';

/**
 * SyncTranscript Component
 *
 * Displays a synchronized transcript with audio playback.
 * Auto-scrolls to keep the active transcript entry centered during playback.
 * Allows clicking entries to seek audio.
 *
 */

type SyncTranscriptProps = {
    callId: string;
    isOpen: boolean;
    onClose: () => void;
    callInfo?: CallInfo;
    callDetailsNavigation?: React.ReactNode;
    isLoading?: boolean;
    useMockData?: boolean; // Enable mock data for testing
};

export const SyncTranscript = ({
    callId,
    isOpen,
    onClose,
    callInfo,
    callDetailsNavigation,
    isLoading,
    useMockData = false,
}: SyncTranscriptProps) => {
    const { getCallTranscript, isCallTranscriptWithRetryLoading, error: transcriptError } = useGetCallTranscript();
    const { getCallAudio, isCallAudioLoading, error: audioError } = useGetCallAudio();

    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [transcriptWithTimes, setTranscriptWithTimes] = useState<TranscriptionWithSpeaker[]>([]);

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const transcriptContainerRef = useRef<HTMLDivElement | null>(null);
    const transcriptEntryRefs = useRef<Map<string, HTMLElement>>(new Map());
    const currentActiveIdRef = useRef<string | null>(null);
    const transcriptWithTimesRef = useRef<TranscriptionWithSpeaker[]>([]);

    // const isDataFetching = isCallAudioLoading || isCallTranscriptWithRetryLoading;

    // Calculate relative timestamps (seconds from start of audio)
    const calculateRelativeTimestamps = useCallback((speeches: CallTranscriptSpeech[]) => {
        if (!speeches.length) return [];

        // Sort by order to ensure correct sequence
        const sortedSpeeches = [...speeches].sort((a, b) => a.order - b.order);

        // Find the earliest timestamp (start of call) - this is our reference point (0 seconds)
        const startTimestamp = Math.min(...sortedSpeeches.map((s) => s.timestamp_ns));

        // IMPORTANT: timestamps represent the END of each speech segment, not the beginning
        // So we need to calculate:
        // - startTime: previous entry's end time (or 0 for first entry, with 2-second default)
        // - endTime: current entry's timestamp
        // - relativeTime: the START time of this entry (for highlighting)

        const entriesWithEndTimes: Array<{ speech: CallTranscriptSpeech; finalEndTime: number }> = [];

        sortedSpeeches.forEach((speech, index) => {
            const endTimeSeconds = (speech.timestamp_ns - startTimestamp) / 1e9;

            let startTimeSeconds: number;
            if (index === 0) {
                startTimeSeconds = 0;
            } else {
                startTimeSeconds = entriesWithEndTimes[index - 1].finalEndTime;
            }

            // Calculate end time with adjustments
            let finalEndTime: number;
            if (index === 0 && endTimeSeconds <= 0.1) {
                // First entry: default 1 second if duration is 0 or near-0
                finalEndTime = 1.0;
            } else {
                finalEndTime = endTimeSeconds;
                // Ensure end time is at least equal to start time (add minimum 0.5s duration if needed)
                if (finalEndTime < startTimeSeconds) {
                    finalEndTime = startTimeSeconds + 0.5;
                }
            }

            entriesWithEndTimes.push({ speech, finalEndTime });
        });

        return entriesWithEndTimes.map(({ speech, finalEndTime }, index) => {
            // Calculate the start time in seconds (when this speech segment begins)
            const startTimeSeconds = index === 0 ? 0 : entriesWithEndTimes[index - 1].finalEndTime;

            return {
                text: speech.text,
                participantIdentity: speech.speaker,
                speakerType: getSpeakerType(speech.speaker),
                timestamp: speech.timestamp_ns,
                id: speech.order.toString(),
                relativeTime: startTimeSeconds as Seconds,
                endTime: finalEndTime as Seconds,
            };
        });
    }, []);

    useEffect(() => {
        if (!isOpen || !callId) return;

        if (useMockData) {
            // Use mock data for testing
            const mockTranscript = getMockTranscript();
            const withTimes = calculateRelativeTimestamps(mockTranscript);
            setTranscriptWithTimes(withTimes);
            transcriptWithTimesRef.current = withTimes;

            // Use the actual OGG audio file
            const mockAudioUrl = getMockAudioUrl();
            setAudioUrl(mockAudioUrl);
        } else {
            getCallAudio(callId, {
                onSuccess: (response) => {
                    const contentType = 'audio/ogg';
                    const audioBlob = new Blob([response], { type: contentType });
                    const url = URL.createObjectURL(audioBlob);
                    setAudioUrl(url);
                },
            });
            getCallTranscript(callId, {
                onSuccess: (response) => {
                    const withTimes = calculateRelativeTimestamps(response.speeches);
                    setTranscriptWithTimes(withTimes);
                    transcriptWithTimesRef.current = withTimes;
                },
            });
        }
    }, [callId, isOpen, calculateRelativeTimestamps, useMockData]);

    useEffect(() => {
        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        };
    }, [audioUrl]);

    // Handle audio time updates - auto-scroll to keep transcript centered
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || !transcriptWithTimes.length) return;

        const onTimeUpdate = () => {
            const currentAudioTime = audio.currentTime;

            let activeId: string | null = null;

            for (let i = 0; i < transcriptWithTimesRef.current.length; i++) {
                const entry = transcriptWithTimesRef.current[i];

                let endTime: number | undefined = undefined;
                if (entry.endTime !== undefined) {
                    endTime = entry.endTime;
                } else {
                    const nextEntry = transcriptWithTimesRef.current[i + 1];
                    if (nextEntry && nextEntry.relativeTime !== undefined) {
                        endTime = nextEntry.relativeTime;
                    }
                }

                if (
                    entry.relativeTime !== undefined &&
                    currentAudioTime >= entry.relativeTime &&
                    (endTime === undefined || currentAudioTime < endTime)
                ) {
                    activeId = entry.id;
                    break;
                }
            }

            if (activeId && activeId !== currentActiveIdRef.current) {
                const activeElement = transcriptEntryRefs.current.get(activeId);
                if (activeElement) {
                    const container = transcriptContainerRef.current;
                    if (container) {
                        // Always scroll to center the active entry
                        activeElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        });
                    }
                }
                currentActiveIdRef.current = activeId;
            }
        };

        audio.addEventListener('timeupdate', onTimeUpdate);

        return () => {
            audio.removeEventListener('timeupdate', onTimeUpdate);
        };
    }, [transcriptWithTimes.length, audioUrl]);

    const handleTranscriptClick = useCallback((relativeTime: Seconds) => {
        const audio = audioRef.current;
        if (audio) {
            audio.currentTime = relativeTime;
            audio.play().catch((error) => {
                console.error('Error playing audio:', error);
            });
        }
    }, []);

    useEffect(() => {
        if (!isOpen) {
            currentActiveIdRef.current = null;
            transcriptEntryRefs.current = new Map();

            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }
        }
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                <div className='flex justify-between items-center'>
                    <h2>Call Transcript</h2>
                    {callDetailsNavigation}
                </div>
            }
            size='lg'
            showDefaultFooter={false}
            footer={
                <Button variant='outline-primary' onClick={onClose}>
                    Close
                </Button>
            }
            isLoadingOverlay={isLoading}
        >
            <div>
                <h3>
                    {useMockData ? getMockCallInfo().name : callInfo?.name || 'Unknown Contact'}
                    {useMockData && <span className='text-sm text-gray-500 ml-2'>(Mock Data)</span>}
                </h3>
                <div className='text-md text-gray-800 flex gap-lg mt-sm'>
                    <span>Date: {useMockData ? getMockCallInfo().callDate : callInfo?.callDate || 'Unknown'}</span>
                    <span>Time: {useMockData ? getMockCallInfo().callTime : callInfo?.callTime || 'Unknown'}</span>
                </div>
            </div>
            <div className='mt-md flex flex-col gap-lg'>
                {!useMockData && isCallTranscriptWithRetryLoading ? (
                    <div className={styles.transcriptEmpty}>Loading transcript...</div>
                ) : !useMockData && (transcriptError || !transcriptWithTimes.length) ? (
                    <div className={`${styles.transcriptEmpty} ${styles.notification}`}>
                        Transcript not available yet - check back later
                    </div>
                ) : transcriptWithTimes.length > 0 ? (
                    <div ref={transcriptContainerRef} className={styles.transcriptContainer}>
                        <Transcripts
                            transcriptions={transcriptWithTimes}
                            onClick={handleTranscriptClick}
                            ref={transcriptEntryRefs}
                        />
                    </div>
                ) : (
                    <div className={styles.transcriptEmpty}>No transcript data available for this call.</div>
                )}

                {!useMockData && isCallAudioLoading ? (
                    <div className={styles.transcriptEmpty}>Loading audio...</div>
                ) : !useMockData && audioError ? (
                    <div className={`${styles.transcriptEmpty} ${styles.notification}`}>
                        Audio not available yet - check back later
                    </div>
                ) : audioUrl ? (
                    <div className={styles.audioContainer}>
                        <audio ref={audioRef} controls className={styles.audioPlayer}>
                            <source src={audioUrl} type='audio/ogg; codecs=opus' />
                            <source src={audioUrl} type='audio/ogg' />
                            <source src={audioUrl} type='audio/mpeg' />
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                ) : (
                    <div className={styles.transcriptEmpty}>No audio recording available for this call.</div>
                )}
            </div>
        </Modal>
    );
};
